import { isEmpty, orderBy } from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  DialogPlugin,
  Drawer,
  Dropdown,
  Form,
  Link,
  MessagePlugin,
  Select,
  Space,
  Table,
  Tag,
} from 'tdesign-react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { produce } from 'immer';
import { useMemoizedFn } from 'ahooks';
import SvgIcon from '@/components/SvgIcon';
import {
  clearBacklog,
  getSubscriptionInternalStats,
  getSubscriptionPartitionList,
  getSubscriptionStats,
} from '@/services/utilService';
import useTable from '@/hooks/useTable';
import JSONModal from '@/components/JSONModal';
import ModalKeyMatch from '@/pages/Cluster/Topic/TopicList/Body/Detail/Subscription/ModalKeyMatch';
import { renderByteRate, renderNum, renderNumRate, toTsOptions } from '@/utils/common';
import usePerms from '@/hooks/usePerms';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';
import BasicInfo from '../BasicInfo';
import Status from '../Status';
import Offset from './Offset';
import Dashboard from './Dashboard';

const { FormItem } = Form;

function Detail({ detail, perms, resource }) {
  const [searchParams] = useSearchParams();
  const clusterName = searchParams.get('name');
  const [visible, setVisible] = useState(false);
  const [partList, setPartList] = useState([]);
  const [form] = Form.useForm();
  const [showChart, setShowChart] = useState(false);
  const [offsetVisible, setOffsetVisible] = useState(false);
  const [currentRow, setCurrentRow] = useState({});
  const partition = Form.useWatch('partition', form);
  const [keyMatchVisible, setKeyMatchVisible] = useState(false);
  const [previewParams, setPreviewParams] = useState({});
  const [hasKey] = usePerms([`${resource}:subPartitionKey`]);

  const confirmBtn = useDisableButton({ content: '确定' });

  const options = [
    {
      content: '重置偏移量',
      resource: `${resource}:subPartitionOffset`,
      value: 1,
    },
    {
      content: '清除积压',
      resource: `${resource}:subPartitionClearBacklog`,
      value: 2,
    },
    {
      content: '订阅详情',
      resource: `${resource}:subPartitionSubDetail`,
      value: 3,
    },
    {
      content: '偏移量详情',
      resource: `${resource}:subPartitionOffsetDetail`,
      value: 4,
    },
  ].filter((item) => perms.includes(item.resource));

  const clickHandler = async (value, row) => {
    setCurrentRow(row);
    if (value === 1) {
      // 重置偏移量
      setOffsetVisible(true);
    }
    if (value === 2) {
      const dialog = DialogPlugin.confirm({
        header: '确认清理积压？',
        theme: 'warning',
        body: (
          <Space breakLine size={[6, 6]}>
            清除Topic
            <Tag>{row.topic}</Tag>
            中订阅名为
            <Tag>{row.subscription}</Tag>
            所有积压
          </Space>
        ),
        confirmBtn,
        onClose: () => dialog.hide(),
        onConfirm: async () => {
          try {
            dialog.update({
              confirmBtn: { loading: true, content: '确定' },
            });
            await clearBacklog({
              clusterName,
              topic: row.topic,
              subName: row.subscription,
            });
            MessagePlugin.success('操作成功！');
            dialog.hide();
          } catch (error) {
            console.trace(error);
            throw error;
          } finally {
            dialog.update({
              confirmBtn: { loading: false, content: '确定' },
            });
          }
        },
      });
    }

    if (value === 3) {
      setPreviewParams({ visible: true, loading: true });
      // 订阅详情
      const { data } = await getSubscriptionStats({
        clusterName,
        topic: row.topic,
        subscription: row.subscription,
      });
      setPreviewParams({
        json: data,
        title: (
          <Header>
            订阅详情
            <HeaderTag>订阅名：{row.subscription}</HeaderTag>
            <HeaderTag>Topic：{row.topic}</HeaderTag>
            <HeaderTag>分区：{row.partition}</HeaderTag>
          </Header>
        ),
        visible: true,
        loading: false,
      });
    }

    if (value === 4) {
      setPreviewParams({ visible: true, loading: true });
      // 偏移量
      const { data } = await getSubscriptionInternalStats({
        clusterName,
        topic: row.topic,
        subscription: row.subscription,
      });
      setPreviewParams({
        json: data,
        title: (
          <Header>
            偏移量详情
            <HeaderTag>分区：{row.partition}</HeaderTag>
          </Header>
        ),
        visible: true,
        loading: false,
      });
    }

    if (value === 6) {
      setKeyMatchVisible(true);
    }
  };

  const columns = [
    // {
    //   colKey: 'subscription',
    //   title: '订阅名',
    //   fixed: 'left',
    //   width: 260,
    // },
    {
      colKey: 'partition',
      title: '分区',
      sorter: true,
      width: 100,
    },
    {
      colKey: 'msgBacklog',
      title: '积压条数',
      sorter: true,
      cell: renderNum,
      width: 120,
    },
    {
      colKey: 'bytesOut',
      title: '消费吞吐',
      sorter: true,
      cell: renderByteRate,
      width: 120,
    },
    {
      colKey: 'rateOut',
      title: '消费速率',
      sorter: true,
      cell: renderNumRate,
      width: 120,
    },
    {
      colKey: 'subscriptionState',
      title: '状态',
      cell: ({ row }) => (
        <Status active={row.subscriptionState}>{row.subscriptionState ? '在线' : '离线'}</Status>
      ),
      width: 100,
    },
    {
      colKey: 'subscriptionType',
      title: '订阅类型',
      width: 110,
    },
    {
      colKey: 'consumerOnlineCount',
      title: '在线个数',
      width: 120,
    },
    {
      colKey: 'updateTime',
      title: '采集时间',
      cell: ({ row }) => dayjs(row.updateTime).format('YYYY-MM-DD HH:mm:ss'),
      width: 200,
    },
    {
      colKey: 'option',
      title: '操作',
      fixed: 'right',
      align: 'center',
      cell: ({ row }) => (
        <Space size='small'>
          <Icon
            type='presentation-chart-line'
            size={18}
            onClick={() => {
              setShowChart(true);
              setCurrentRow(row);
            }}
          />
          <Dropdown
            options={produce(options, (draft) => {
              const hasKeyShared = (row.subscriptionType || '').toLowerCase() === 'key_shared';
              if (hasKeyShared && hasKey) {
                draft.splice(4, 0, { content: '查看key归属', value: 6 });
              }
            })}
            onClick={({ value }) => clickHandler(value, row)}
          >
            <span>
              <Icon type='dots-vertical' />
            </span>
          </Dropdown>
        </Space>
      ),
      width: 80,
    },
  ];

  const loadData = useMemoizedFn(async (params) => {
    const { data } = await getSubscriptionPartitionList({
      ...params,
      clusterName,
      topic: detail.topic,
      subscription: detail.subscription,
    });
    setData(orderBy(data, (item) => Number(item.partition), 'asc'));
  });

  const { config, setData, data, doSearch } = useTable({
    columns,
    onSearch: loadData,
  });

  useEffect(() => {
    if (visible) {
      doSearch({ partition });
    } else {
      // 关闭后初始化
      form.reset();
      setShowChart(false);
      setKeyMatchVisible(false);
      setOffsetVisible(false);
      setPartList([]);
      setPreviewParams({});
      setData([]);
      setCurrentRow({});
    }
  }, [doSearch, partition, visible, form, setData]);

  useEffect(() => {
    if (visible && !isEmpty(data) && isEmpty(partList) && !partition) {
      // 初始化option
      setPartList(data.map((item) => ({ label: item.partition, value: item.partition })));
    }
  }, [data, partList, partition, visible]);

  const handleClose = () => setVisible(false);

  const onRefresh = () => {
    doSearch({ partition });
  };

  const summary = (
    <Root key={1} className='fadeIn'>
      <BasicInfo detail={detail} />
      <Form form={form} layout='inline' style={{ marginBottom: 20, marginTop: 14 }}>
        <FormItem name='partition'>
          <Select options={toTsOptions(partList)} placeholder='请选择分区' clearable />
        </FormItem>
      </Form>
      <Table {...config} />
    </Root>
  );

  return (
    <>
      {detail.isPartitioned ? (
        <Link theme='primary' style={{ wordBreak: 'break-all' }} onClick={() => setVisible(true)}>
          {detail.subscription}
        </Link>
      ) : (
        detail.subscription
      )}
      <Drawer
        header='订阅-分区级别'
        size={1000}
        footer={null}
        onClose={handleClose}
        onOverlayClick={handleClose}
        placement='right'
        destroyOnClose
        visible={visible}
      >
        {summary}
      </Drawer>
      <Dashboard
        clusterName={clusterName}
        visible={showChart}
        detail={currentRow}
        handleClose={() => setShowChart(false)}
      />
      <Offset
        onRefresh={onRefresh}
        visible={offsetVisible}
        detail={{ ...currentRow, clusterName }}
        handleClose={() => setOffsetVisible(false)}
      />
      <JSONModal {...previewParams} handleClose={() => setPreviewParams({})} />
      <ModalKeyMatch
        visible={keyMatchVisible}
        handleClose={() => setKeyMatchVisible(false)}
        params={{
          clusterName,
          isPartition: false, // 进去分区之后传false
          subscription: currentRow.subscription,
          topic: currentRow.topic,
        }}
      />
    </>
  );
}

export default connect((state) => ({
  perms: state.auth.perms,
}))(Detail);

const Icon = styled(SvgIcon)`
  cursor: pointer;
  color: #194dff;
`;

const Root = styled.div``;

const Header = styled.div`
  display: flex;
`;

const HeaderTag = styled.div`
  background: rgba(102, 136, 255, 0.12);
  border-radius: 6px;
  font-weight: 400;
  font-size: 12px;
  line-height: 20px;
  padding: 2px 10px;
  color: #6688ff;
  margin-left: 12px;
`;
